import { cn } from "app/lib/utils"
import type {
  CircleButtonSize,
  PlayerControlSize,
} from "app/player/controlSizes"
import { motion, useReducedMotion } from "framer-motion"
import { ComponentProps } from "react"
import PlayerCircleButton from "./PlayerCircleButton"
import PlayerIcon from "./PlayerIcon"

const MotionPlayerCircleButton = motion(PlayerCircleButton)

const EXIT_TRANSITION = {
  duration: 0.25,
  ease: "easeOut" as const,
}

/** Density → hero circle size + thick play icon. */
const HERO: Record<
  PlayerControlSize,
  {
    size: CircleButtonSize
    icon: { src: string; size: number; className: string }
  }
> = {
  sm: {
    size: "md",
    icon: {
      src: "/icons/player/thick/ico_play.svg",
      size: 38,
      className: "ml-[7px]",
    },
  },
  md: {
    size: "lg",
    icon: {
      src: "/icons/player/thick/ico_play.svg",
      size: 48,
      className: "ml-[10px]",
    },
  },
}

type Props = Omit<
  ComponentProps<typeof MotionPlayerCircleButton>,
  "initial" | "animate" | "exit" | "transition" | "children" | "color" | "size"
> & {
  size?: PlayerControlSize
}

/** Desktop initial/hero play — large circle, play-only. */
export default function VideoHeroPlayButton({
  className,
  onClick,
  size = "md",
  ...props
}: Props) {
  const shouldReduceMotion = useReducedMotion()
  const hero = HERO[size]

  return (
    <MotionPlayerCircleButton
      color="actie"
      data-id="large-play-button"
      size={hero.size}
      className={className}
      onClick={onClick}
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: shouldReduceMotion ? 1 : 0 }}
      transition={shouldReduceMotion ? { duration: 0 } : EXIT_TRANSITION}
      {...props}
    >
      <PlayerIcon
        src={hero.icon.src}
        size={hero.icon.size}
        className={cn(hero.icon.className)}
      />
    </MotionPlayerCircleButton>
  )
}
